import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';
import { EN, HI, HINGLISH, type StringKey } from './strings';

/**
 * The rider's language: English, Hinglish (the mockup's own copy, the default) or Hindi.
 * Hindi is only partly translated: a missing Hindi string falls back to Hinglish, then English (see strings.ts).
 * The choice is kept on this phone (localStorage `zr-lang`) — the API has no language field yet.
 */
export type Lang = 'en' | 'hinglish' | 'hi';
export const LANGS: Lang[] = ['en', 'hinglish', 'hi'];
export const DEFAULT_LANG: Lang = 'hinglish';
const KEY = 'zr-lang';

export type Params = Record<string, string | number>;

const fill = (s: string, p?: Params) => (p ? s.replace(/\{(\w+)\}/g, (m, k: string) => (k in p ? String(p[k]) : m)) : s);

/** Look a string up in the language, falling back hi → hinglish → en. Exported for tests. */
export function lookup(lang: Lang, key: StringKey): string {
  if (lang === 'hi') return HI[key] ?? HINGLISH[key] ?? EN[key];
  if (lang === 'hinglish') return HINGLISH[key] ?? EN[key];
  return EN[key];
}

/** Whether Hindi has its own line for this key (false = shown from the Hinglish/English fallback). */
export const hasHindi = (key: StringKey) => key in HI;

export function translate(lang: Lang, key: StringKey, p?: Params): string {
  return fill(lookup(lang, key), p);
}

/** Plural pick: `<key>.one` when n === 1, else `<key>.other`; `{n}` is filled in. */
export function plural(lang: Lang, key: string, n: number, p?: Params): string {
  const k = (n === 1 ? `${key}.one` : `${key}.other`) as StringKey;
  return fill(lookup(lang, k), { n: n.toLocaleString('en-IN'), ...p });
}

export function readLang(): Lang {
  try {
    const v = localStorage.getItem(KEY);
    return v === 'en' || v === 'hinglish' || v === 'hi' ? v : DEFAULT_LANG;
  } catch {
    return DEFAULT_LANG;
  }
}

export type T = ((key: StringKey, p?: Params) => string) & { n: (key: string, n: number, p?: Params) => string; lang: Lang };

export function makeT(lang: Lang): T {
  const t = ((key: StringKey, p?: Params) => translate(lang, key, p)) as T;
  t.n = (key, n, p) => plural(lang, key, n, p);
  t.lang = lang;
  return t;
}

type Ctx = { lang: Lang; setLang: (l: Lang) => void; t: T };
const LangCtx = createContext<Ctx | null>(null);

export function LangProvider({ children, initial }: { children: ReactNode; initial?: Lang }) {
  const [lang, setLangState] = useState<Lang>(() => initial ?? readLang());
  const setLang = useCallback((l: Lang) => {
    setLangState(l);
    try {
      localStorage.setItem(KEY, l);
    } catch {
      /* this tab only */
    }
    document.documentElement.lang = l === 'hi' ? 'hi' : 'en-IN';
  }, []);
  const value = useMemo(() => ({ lang, setLang, t: makeT(lang) }), [lang, setLang]);
  return <LangCtx.Provider value={value}>{children}</LangCtx.Provider>;
}

const fallback: Ctx = { lang: DEFAULT_LANG, setLang: () => undefined, t: makeT(DEFAULT_LANG) };
/** Strings for the current language (works outside the provider too, in Hinglish — handy in tests). */
export const useLang = (): Ctx => useContext(LangCtx) ?? fallback;
export const useT = (): T => useLang().t;
